// AIDEV-NOTE: Todos os metodos sao async e funcionam 100% offline (client-side only)
// Usa @react-pdf/renderer para gerar Blob do PDF inteiramente no navegador

import { createElement, type ReactElement } from 'react';
import { pdf, type DocumentProps } from '@react-pdf/renderer';
import { PedidoPDF } from '../pdf/PedidoPDF';
import type { Pedido } from '@azoupvendas/shared';

// -- Interfaces publicas reutilizadas pelo modulo --

export interface EmpresaData {
  nome_fantasia: string;
  cnpj: string;
  fone?: string;
  logo_url?: string;
  endereco?: string;
}

export interface VendedorData {
  nome: string;
}

// AIDEV-NOTE: Limite de tempo da geracao. Em 2026-09 um bug de paginacao do @react-pdf/renderer
// 3.x fazia o toBlob() nunca resolver: o modal ficava "carregando" para sempre, sem erro, e no
// iPad a aba morria. A lib foi corrigida (4.9.0), mas sem limite qualquer travamento futuro volta
// a ser invisivel. 90s e folgado de proposito: pedido de 300 itens leva ~12s em desktop e bem
// mais em tablet antigo — o limite existe para pegar travamento, nao para cortar pedido grande.
export const TIMEOUT_GERACAO_PDF_MS = 90_000;

function comLimiteDeTempo<T>(promessa: Promise<T>, ms: number, mensagem: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(mensagem)), ms);
    promessa.then(
      (valor) => {
        clearTimeout(timer);
        resolve(valor);
      },
      (erro) => {
        clearTimeout(timer);
        reject(erro);
      }
    );
  });
}

// -- Defaults para campos opcionais --

const EMPRESA_DEFAULTS: EmpresaData = {
  nome_fantasia: 'Empresa',
  cnpj: ''
};

function resolveEmpresa(partial?: Partial<EmpresaData>): EmpresaData {
  return { ...EMPRESA_DEFAULTS, ...partial };
}

// -- Helper: gera nome do arquivo --

function gerarNomeArquivo(pedido: Pedido): string {
  const numero = pedido.numero ?? 'rascunho';
  const data = pedido.created_at
    ? new Date(pedido.created_at).toISOString().slice(0, 10)
    : new Date().toISOString().slice(0, 10);
  return `pedido-${numero}-${data}.pdf`;
}

// -- Helper: monta texto resumo para compartilhamento --

function gerarTextoResumo(pedido: Pedido, empresa: EmpresaData): string {
  const numero = pedido.numero ? `N. ${pedido.numero}` : '(Rascunho)';
  const total = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(pedido.total_final);

  return [
    `Pedido ${numero} - ${empresa.nome_fantasia}`,
    `Cliente: ${pedido.cliente_nome}`,
    `Total: ${total}`,
    `Itens: ${pedido.itens?.length ?? 0}`
  ].join('\n');
}

// -- Service --

export class PDFService {
  /**
   * Gera o Blob PDF a partir dos dados do pedido.
   */
  async gerarBlob(
    pedido: Pedido,
    empresaPartial?: Partial<EmpresaData>,
    vendedor?: VendedorData
  ): Promise<Blob> {
    const empresa = resolveEmpresa(empresaPartial);
    const vendedorResolved: VendedorData = vendedor ?? { nome: 'Vendedor' };

    // AIDEV-NOTE: createElement necessario porque pdf() espera ReactElement, nao JSX direto em .ts
    // Cast necessario: PedidoPDF retorna <Document> internamente, mas TS nao consegue inferir
    const element = createElement(PedidoPDF, {
      pedido,
      empresa,
      vendedor: vendedorResolved
    }) as unknown as ReactElement<DocumentProps>;

    const doc = pdf(element);
    return comLimiteDeTempo(
      doc.toBlob(),
      TIMEOUT_GERACAO_PDF_MS,
      'Não foi possível gerar o PDF deste pedido: a geração excedeu o tempo limite. ' +
        'Tente novamente e, se continuar, avise o suporte.'
    );
  }

  /**
   * Compartilha via WhatsApp.
   * Tenta Web Share API com arquivo PDF; fallback para link wa.me com texto.
   * AIDEV-NOTE: navigator.share() abre share sheet generico do SO. Fallback e link wa.me direto.
   */
  async compartilharWhatsApp(
    pedido: Pedido,
    empresaPartial?: Partial<EmpresaData>,
    vendedor?: VendedorData,
    telefone?: string
  ): Promise<void> {
    const empresa = resolveEmpresa(empresaPartial);
    const texto = gerarTextoResumo(pedido, empresa);

    // Tenta Web Share API com arquivo
    if (navigator.share && navigator.canShare) {
      try {
        const blob = await this.gerarBlob(pedido, empresaPartial, vendedor);
        const file = new File([blob], gerarNomeArquivo(pedido), {
          type: 'application/pdf'
        });

        const shareData: ShareData = {
          title: `Pedido ${pedido.numero ?? 'Rascunho'}`,
          text: texto,
          files: [file]
        };

        if (navigator.canShare(shareData)) {
          await navigator.share(shareData);
          return;
        }
      } catch (err) {
        // AIDEV-NOTE: Se usuario cancelar o share, AbortError e lancado — nao e erro real
        if (err instanceof Error && err.name === 'AbortError') return;
        // Fallback abaixo
      }
    }

    // Fallback: link wa.me
    const textoEncoded = encodeURIComponent(texto);
    const fone = telefone ? telefone.replace(/\D/g, '') : '';
    const url = fone
      ? `https://wa.me/${fone}?text=${textoEncoded}`
      : `https://wa.me/?text=${textoEncoded}`;

    window.open(url, '_blank', 'noopener,noreferrer');
  }

  /**
   * Compartilha via Email.
   * Tenta Web Share API; fallback para mailto: com subject e body pre-preenchidos.
   */
  async compartilharEmail(
    pedido: Pedido,
    empresaPartial?: Partial<EmpresaData>,
    vendedor?: VendedorData
  ): Promise<void> {
    const empresa = resolveEmpresa(empresaPartial);
    const texto = gerarTextoResumo(pedido, empresa);
    const subject = `Pedido ${pedido.numero ?? 'Rascunho'} - ${empresa.nome_fantasia}`;

    // Tenta Web Share API com arquivo
    if (navigator.share && navigator.canShare) {
      try {
        const blob = await this.gerarBlob(pedido, empresaPartial, vendedor);
        const file = new File([blob], gerarNomeArquivo(pedido), {
          type: 'application/pdf'
        });

        const shareData: ShareData = {
          title: subject,
          text: texto,
          files: [file]
        };

        if (navigator.canShare(shareData)) {
          await navigator.share(shareData);
          return;
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
      }
    }

    // Fallback: mailto
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(texto)}`;
    window.location.href = mailtoUrl;
  }

  /**
   * Faz download do PDF.
   * Cria Blob URL, dispara click em anchor oculto, revoga URL apos 1s.
   * AIDEV-NOTE: setTimeout necessario — revogar antes do download cancela o arquivo
   */
  async download(
    pedido: Pedido,
    empresaPartial?: Partial<EmpresaData>,
    vendedor?: VendedorData
  ): Promise<void> {
    const blob = await this.gerarBlob(pedido, empresaPartial, vendedor);
    const url = URL.createObjectURL(blob);

    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = gerarNomeArquivo(pedido);
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  /**
   * Abre pre-visualizacao do PDF em nova aba.
   */
  async previsualizar(
    pedido: Pedido,
    empresaPartial?: Partial<EmpresaData>,
    vendedor?: VendedorData
  ): Promise<void> {
    const blob = await this.gerarBlob(pedido, empresaPartial, vendedor);
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');

    // AIDEV-NOTE: Revogamos apos 60s para dar tempo do navegador carregar o PDF na aba
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }
}

// Singleton para uso em toda a aplicacao
export const pdfService = new PDFService();
